import {
  GAME_ID,
  SCREENS,
  OBJECT_PLACEMENT_TYPES,
  OBJECT_PLACEMENT_CORRESPONDING_ANSWERS,
  TARGET_AREAS,
  REDUX_ACTIONS,
  OBJECT_PLACEMENT_METHODS,
  OBJECT_PLACEMENT_METHOD,
} from '../../data/constants';
import { selectCurrentScene, selectCurrentObject, selectNumScenesInLevel } from './curriculumSelectors';
import { shuffleArray } from '../../../../lib/utilityFunctions';

const initialState = {
  currentLevelIndex: 0,
  currentSceneIndex: 0,
  currentObjectIndex: 0,
  activeSceneObjects: [[], []],
  currentSceneObjectAnswers: {},
  // for random placement of A/B scenes
  leftIsA: true,
  currentHoverTarget: TARGET_AREAS.NONE,
  roundIsComplete: false,
  incrementScene: false,
};

const setUpSceneObjectAnswers = (level, scene, placementInfo, leftIsA) => {
  const objectAnswers = {};
  let currentObject = '';
  for (let i = 0; i < selectCurrentScene(level, scene).objects.length; i++) {
    currentObject = selectCurrentScene(level, scene).objects[i].objectId;
    if (placementInfo[0].includes(currentObject)) {
      if (placementInfo[1].includes(currentObject)) {
        objectAnswers[currentObject] = OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.BOTH;
      } else {
        objectAnswers[currentObject] = leftIsA ?
          OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.A :
          OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.B;
      }
    } else if (placementInfo[1].includes(currentObject)) {
      objectAnswers[currentObject] = leftIsA ?
        OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.B :
        OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.A;
    } else {
      objectAnswers[currentObject] = OBJECT_PLACEMENT_CORRESPONDING_ANSWERS.NONE;
    }
  }
  return objectAnswers;
};

const setUpSceneObjectPlacement = (level, scene, leftIsA) => {
  const objectPlacementInfo = [[], []];
  // If we are doing full random, check here and use randomPlacementOptions
  let placementArray = selectCurrentScene(level, scene).objects;
  if (OBJECT_PLACEMENT_METHOD === OBJECT_PLACEMENT_METHODS.FULL_RANDOM) {
    placementArray = shuffleArray(selectCurrentScene(level, scene).randomPlacementOptions);
    // modify structure of random array to match objects
    placementArray = placementArray.map(value => ({ defaultPlacement: value }));
  }
  // Not full random must be default or random side, so use this below logic with default placements
  for (let i = 0; i < selectCurrentScene(level, scene).objects.length; i++) {
    switch (placementArray[i].defaultPlacement) {
      case OBJECT_PLACEMENT_TYPES.A:
        if (leftIsA) {
          objectPlacementInfo[0].push(selectCurrentScene(level, scene).objects[i].objectId);
        } else {
          objectPlacementInfo[1].push(selectCurrentScene(level, scene).objects[i].objectId);
        }
        break;
      case OBJECT_PLACEMENT_TYPES.B:
        if (leftIsA) {
          objectPlacementInfo[1].push(selectCurrentScene(level, scene).objects[i].objectId);
        } else {
          objectPlacementInfo[0].push(selectCurrentScene(level, scene).objects[i].objectId);
        }
        break;
      case OBJECT_PLACEMENT_TYPES.BOTH:
        objectPlacementInfo[0].push(selectCurrentScene(level, scene).objects[i].objectId);
        objectPlacementInfo[1].push(selectCurrentScene(level, scene).objects[i].objectId);
        break;
      default:
        break;
    }
  }
  return objectPlacementInfo;
};

const roundPlayReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case REDUX_ACTIONS.CHANGE_SCREEN: {
      if (action.payload === SCREENS.PROGRESS) {
        return initialState;
      }
      return state;
    }
    case REDUX_ACTIONS.RESET_ROUND: {
      return initialState;
    }
    case REDUX_ACTIONS.START_ROUND: {
      let { leftIsA } = state;
      if (OBJECT_PLACEMENT_METHOD === OBJECT_PLACEMENT_METHODS.RANDOM_SIDE) {
        leftIsA = Math.round(Math.random()) === 0 ? state.leftIsA : !state.leftIsA;
      }
      const placementInfo = setUpSceneObjectPlacement(action.payload, 0, leftIsA);
      return {
        ...initialState,
        leftIsA,
        activeSceneObjects: placementInfo,
        currentLevelIndex: action.payload,
        currentSceneObjectAnswers: setUpSceneObjectAnswers(action.payload, 0, placementInfo, leftIsA),
      };
    }
    case REDUX_ACTIONS.ENABLE_SCENE_ELEMENT: {
      const newState = { ...state };
      if (action.payload.isSceneA && !state.activeSceneObjects[0].includes(action.payload.elementId)) {
        newState.activeSceneObjects[0] = [...state.activeSceneObjects[0], action.payload.elementId];
      } else if (!action.payload.isSceneA && !state.activeSceneObjects[1].includes(action.payload.elementId)) {
        newState.activeSceneObjects[1] = [...state.activeSceneObjects[1], action.payload.elementId];
      }
      return newState;
    }
    case REDUX_ACTIONS.INCREMENT_OBJECT_INDEX: {
      return {
        ...state,
        currentObjectIndex: state.currentObjectIndex + 1,
      };
    }
    case REDUX_ACTIONS.SET_CURRENT_HOVER_TARGET: {
      return {
        ...state,
        currentHoverTarget: action.payload,
      };
    }
    case REDUX_ACTIONS.MAKE_SELECTION: {
      const currentObject = selectCurrentObject(
        state.currentLevelIndex,
        state.currentSceneIndex,
        state.currentObjectIndex,
      ).objectId;
      if (action.payload === state.currentSceneObjectAnswers[currentObject]) {
        // CORRECT! > Enable object in scene and increment object index.
        const newActiveObjects = state.activeSceneObjects.slice();
        // Check where we should enable based on isLeftA.
        if (action.payload === OBJECT_PLACEMENT_TYPES.A) {
          if (state.leftIsA) {
            newActiveObjects[0].push(currentObject);
          } else {
            newActiveObjects[1].push(currentObject);
          }
        } else if (action.payload === OBJECT_PLACEMENT_TYPES.B) {
          if (state.leftIsA) {
            newActiveObjects[1].push(currentObject);
          } else {
            newActiveObjects[0].push(currentObject);
          }
        }
        // check if we need to end the round
        if (state.currentObjectIndex === (
          selectCurrentScene(state.currentLevelIndex, state.currentSceneIndex).objects.length - 1
        )) {
          // Next Scene or End Round
          // check for end round
          if (state.currentSceneIndex === selectNumScenesInLevel(state.currentLevelIndex) - 1) {
            return {
              ...state,
              roundIsComplete: true,
            };
          }
          // time for next scene!
          return {
            ...state,
            incrementScene: true,
          };
        }
        // no next scene or end round, just increment and update shown objects
        return {
          ...state,
          currentObjectIndex: state.currentObjectIndex + 1,
          activeSceneObjects: newActiveObjects,
        };
      }
      break;
    }
    case REDUX_ACTIONS.INCREMENT_SCENE_INDEX: {
      let { leftIsA } = state;
      if (OBJECT_PLACEMENT_METHOD === OBJECT_PLACEMENT_METHODS.RANDOM_SIDE) {
        leftIsA = Math.round(Math.random()) === 0 ? state.leftIsA : !state.leftIsA;
      }
      const placementInfo = setUpSceneObjectPlacement(
        state.currentLevelIndex,
        state.currentSceneIndex + 1,
        leftIsA,
      );
      return {
        ...state,
        currentSceneIndex: state.currentSceneIndex + 1,
        currentObjectIndex: 0,
        activeSceneObjects: placementInfo,
        currentSceneObjectAnswers: setUpSceneObjectAnswers(
          state.currentLevelIndex,
          state.currentSceneIndex + 1,
          placementInfo,
          leftIsA,
        ),
        incrementScene: false,
        leftIsA,
      };
    }
    default: {
      return state;
    }
  }
  return state;
};

export default roundPlayReducer;
